import { Button } from '~/components/primitives/Button'
import { ANCHOR_COPY } from '~/models/routineAnchor'
import type { AnchorPrompt } from '~/services/routineAnchorService'
import styles from './AnchorPrompts.module.css'

/**
 * Routine anchors on Today (D-099).
 *
 * **The copy must never imply an interruption.** There is nowhere to push from
 * until D-095, so an anchor changes what this screen shows and nothing else. The
 * region says so out loud rather than leaving the user to discover it at 07:30
 * when nothing happens — stating the limit is what makes the feature honest
 * instead of broken.
 *
 * **The region is stable once any anchor is configured.** It says "nothing open
 * right now" rather than disappearing: a block that comes and goes from day to
 * day cannot be learned by muscle memory, which is the whole reason Today's
 * layout is fixed. It is absent entirely for a user who has configured nothing,
 * on the D-096 precedent — no empty task for someone who never asked.
 *
 * **No count, no badge, no streak, and no "2 of 6".** These are things to do, not
 * a score to be measured against.
 */

interface AnchorPromptsProps {
  prompts: readonly AnchorPrompt[]
  onSnooze: (prompt: AnchorPrompt) => void
  onNotToday: (prompt: AnchorPrompt) => void
  busy: boolean
}

export function AnchorPrompts({ prompts, onSnooze, onNotToday, busy }: AnchorPromptsProps) {
  return (
    <section class={styles.region} aria-labelledby="anchors-heading">
      <h2 id="anchors-heading" class={styles.heading}>
        {ANCHOR_COPY.regionHeading}
      </h2>

      {prompts.length === 0 ? (
        <p class={styles.body}>{ANCHOR_COPY.nothingOpen}</p>
      ) : (
        <ul class={styles.list}>
          {prompts.map((prompt) => (
            <li key={prompt.kind} class={styles.prompt}>
              <p class={styles.body}>{prompt.note}</p>
              <div class={styles.actions}>
                <Button
                  variant="secondary"
                  busy={busy}
                  onClick={() => onSnooze(prompt)}
                  label={`${ANCHOR_COPY.snooze} ${prompt.label.toLowerCase()}`}
                >
                  {ANCHOR_COPY.snooze}
                </Button>
                <Button
                  variant="secondary"
                  busy={busy}
                  onClick={() => onNotToday(prompt)}
                  label={`${ANCHOR_COPY.notToday} for ${prompt.label.toLowerCase()}`}
                >
                  {ANCHOR_COPY.notToday}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p class={styles.limit}>{ANCHOR_COPY.cannotNotify}</p>
    </section>
  )
}
